import { z } from 'zod'

import {
  ERROR_CODE,
  type ErrorCode,
  type ErrorFamily,
  errorFamily,
  isErrorCode,
  isRetryableByDefault,
} from './codes'

/**
 * Shared API error envelope.
 *
 * Wire shape, from `docs/design/backend-contracts-v0.1.md` §3.3:
 *
 * ```json
 * { "error": { "code": "...", "message": "...", "retryable": false, "request_id": "uuid" } }
 * ```
 *
 * Privacy contract (§9): an envelope must never carry learner answers, writing
 * content, audio, PINs, tokens, signed URLs or raw provider payloads. The
 * envelope deliberately has no `details` or `payload` field so there is no
 * sanctioned place to put them.
 */

export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string().min(1),
    message: z.string(),
    retryable: z.boolean().optional(),
    request_id: z.string().optional(),
  }),
})

export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>

export interface AppErrorOptions {
  readonly message?: string
  readonly retryable?: boolean
  readonly requestId?: string
  readonly cause?: unknown
}

/**
 * Generic message used whenever the server did not supply a safe one. Kept
 * deliberately vague so an authorization failure cannot be used to probe
 * whether another family's resource exists (§3.3, threat table §10).
 */
const GENERIC_MESSAGE = 'Something went wrong. Please try again.'

export class AppError extends Error {
  readonly code: ErrorCode
  readonly family: ErrorFamily
  readonly retryable: boolean
  readonly requestId: string | undefined

  constructor(code: ErrorCode, options: AppErrorOptions = {}) {
    super(
      options.message ?? GENERIC_MESSAGE,
      options.cause === undefined ? undefined : { cause: options.cause },
    )
    this.name = 'AppError'
    this.code = code
    this.family = errorFamily(code)
    this.retryable = options.retryable ?? isRetryableByDefault(code)
    this.requestId = options.requestId
  }

  /** Serialize back to the wire envelope. */
  toEnvelope(): ErrorEnvelope {
    return {
      error: {
        code: this.code,
        message: this.message,
        retryable: this.retryable,
        ...(this.requestId === undefined ? {} : { request_id: this.requestId }),
      },
    }
  }
}

export function createErrorEnvelope(
  code: ErrorCode,
  options: AppErrorOptions = {},
): ErrorEnvelope {
  return new AppError(code, options).toEnvelope()
}

/**
 * Parse an unknown value into an {@link AppError}.
 *
 * Returns `null` when the value is not a recognizable error envelope, so a
 * caller can distinguish "the server reported a contract error" from "the
 * request failed in some other way".
 *
 * An envelope carrying a code this client build does not know about is still
 * parsed, but is downgraded to `INTERNAL_ERROR`. Forward compatibility matters
 * more than rejecting a newer server (§8).
 */
export function parseErrorEnvelope(value: unknown): AppError | null {
  const parsed = errorEnvelopeSchema.safeParse(value)
  if (!parsed.success) return null

  const { code, message, retryable, request_id: requestId } = parsed.data.error
  const known: ErrorCode = isErrorCode(code) ? code : ERROR_CODE.INTERNAL_ERROR

  return new AppError(known, {
    message: message.length > 0 ? message : GENERIC_MESSAGE,
    ...(retryable === undefined ? {} : { retryable }),
    ...(requestId === undefined ? {} : { requestId }),
  })
}

/**
 * Coerce any thrown value into an {@link AppError} so UI and logging code has
 * a single shape to handle. Unknown throwables become `INTERNAL_ERROR` and
 * their original message is dropped rather than shown to the user, since it
 * may contain provider or database text.
 */
export function toAppError(value: unknown): AppError {
  if (value instanceof AppError) return value

  const fromEnvelope = parseErrorEnvelope(value)
  if (fromEnvelope) return fromEnvelope

  return new AppError(ERROR_CODE.INTERNAL_ERROR, { cause: value })
}
